import { useState, useCallback } from "react";
import { Apis } from "../../lib/Apis";

// --- Hook from Blog/useHook.js ---
export const useBlog = () => {
    const getBlogCategories = async (limit = 100) => {
        try {
            // Fetch all categories for the blog form dropdown (not paginated list view)
            const response = await Apis.getBlogCategories(0, limit)
            // API shape: { data: [...], pagination: {...} }
            if (Array.isArray(response?.data?.data)) {
                return response.data.data
            }
            if (Array.isArray(response?.data)) {
                return response.data
            }
            return []
        } catch (error) {
            console.error("Error fetching blog categories:", error)
            throw error
        }
    }
    const postBlog = async (data) => {
        try {
            const response = await Apis.postBlog(data)
            return response.data
        } catch (error) {
            console.error("Error posting blog:", error)
        }
    }
    const updateBlog = async (id, data) => {
        try {
            const response = await Apis.updateBlog(id, data)
            return response.data
        } catch (error) {
            console.error("Error updating blog:", error)
            throw error
        }
    }
    const getBlogs = async (paginationParams = {}, searchParams = {}) => {
        try {
            const params = { ...paginationParams, ...searchParams };
            const response = await Apis.getBlogs(params)
            return {
                data: response.data.data,
                pagination: response.data.pagination
            }
        } catch (error) {
            console.error("Error fetching blogs:", error)
            throw error
        }
    }

    const searchBlogs = async (paginationParams = {}, searchParams = {}) => {
        try {
            const params = { ...paginationParams, ...searchParams };
            const response = await Apis.getBlogs(params);
            return {
                data: response.data.data,
                pagination: response.data.pagination
            };
        } catch (error) {
            console.error("🚀 ~ file: useHook.js ~ line 64 ~ searchBlogs ~ error", error);
            return error;
        }
    };
    const getBlogById = async (id) => {
        try {
            const response = await Apis.getBlogById(id)
            return response.data
        } catch (error) {
            console.error("Error fetching blog by id:", error)
            throw error
        }
    }
    const addPopularBlog = async (data) => {
        try {
            const response = await Apis.addPopularBlog(data)
            return response.data
        } catch (error) {
            console.error("Error adding popular blog:", error)
            throw error
        }
    }
    const addOnHeaderBlog = async (data) => {
        try {
            const response = await Apis.addOnHeaderBlog(data)
            return response.data
        } catch (error) {
            console.error("Error adding on header blog:", error)
            throw error
        }
    }
    const deleteBlog = async (id) => {
        try {
            const response = await Apis.deleteBlog(id)
            return response.data
        } catch (error) {
            console.error("Error deleting blog:", error)
            throw error
        }
    }
    const showBlogOnHomeScreen = async (blogId, homeScreen) => {
        try {
            const data = {
                blogId,
                homeScreen: homeScreen ? 1 : 0
            };
            const response = await Apis.addBlogToHomeScreen(data);
            return response;
        } catch (error) {
            console.error("Error updating home screen status:", error);
            return error;
        }
    };

    const updateBlogPosition = async (blogId, newPosition) => {
        try {
            const response = await Apis.updateBlogPosition({ blogId: Number(blogId), newPosition });
            return response?.data;
        } catch (error) {
            console.error("Error updating blog position:", error);
            throw error;
        }
    };

    return {
        getBlogCategories,
        postBlog,
        updateBlog,
        getBlogs,
        getBlogById,
        addPopularBlog,
        addOnHeaderBlog,
        deleteBlog,
        showBlogOnHomeScreen,
        searchBlogs,
        updateBlogPosition
    }
}

// --- Hook from BlogCategories/useHook.js ---
export const useBlogCategory = () => {
    const addBlogCategories = async (data) => {
        try {
            const response = await Apis.addCategoryBlog(data);
            return response.data;
        } catch (error) {
            console.error("Error adding blog category:", error);
            throw error;
        }
    }
    const getBlogCategories = async (setBlogCategories, setTotal, offset = 0, limit = 10, name = "", status = "") => {
        try {
            const params = {};
            
            if (name && name.trim()) {
                params.name = name.trim();
            }
            
            if (status !== "") {
                params.status = status === "true" ? true : false;
            }

            const response = await Apis.getBlogCategories(offset, limit, params);
            if (response?.status === 200) {
                setBlogCategories(response.data.data);
                // Extract total from pagination object
                const totalRecords = response.data.pagination?.totalCount || response.data.data.length;
                setTotal(totalRecords);
                return response.data;
            } else {
                throw new Error(response?.data?.message || "Failed to fetch blog categories.");
            }
        } catch (error) {
            console.error("Error fetching blog categories:", error);
            throw error;
        }
    }

    const searchBlogCategories = async (name, status, offset = 0, limit = 10) => {
        try {
            const params = {};
            
            if (name && name.trim()) {
                params.name = name.trim();
            }
            
            if (status !== "") {
                params.status = status === "true" ? true : false;
            }

            const response = await Apis.getBlogCategories(offset, limit, params);
            return response.data;
        } catch (error) {
            console.error("🚀 ~ file: useHook.js ~ line 64 ~ searchBlogCategories ~ error", error);
            return error;
        }
    };
    const updateBlogCategory = async (id, data) => {
        try {
            const response = await Apis.updateBlogCategory(id, data);
            return response.data;
        } catch (error) {
            console.error("Error updating blog category:", error);
            throw error;
        }
    }
    const deleteBlogCategory = async (id) => {
        try {
            const response = await Apis.deleteBlogCategory(id);
            return response.data;
        } catch (error) {
            console.error("Error deleting blog category:", error);
            throw error;
        }
    }
    const updateCategoryStatus = async (id, status) => {
        try {
            const response = await Apis.updateBlogCategoryStatus(id, status);
            return response.data;
        } catch (error) {
            console.error("Error updating category status:", error);
            throw error;
        }
    };
    const getBlogByCategoryId = async (id, setBlogs) => {
        try {
            const response = await Apis.getBlogCategoryById(id);
            setBlogs(response.data.blogs);
            return response;
        } catch (error) {
            console.error("Error fetching blog category by id:", error);
            throw error;
        }
    };
    const updateBlogCategoryPosition = async (categoryId, newPosition) => {
        try {
            const response = await Apis.updateBlogCategoryPosition({ categoryId, newPosition });
            return response?.data;
        } catch (error) {
            console.error("Error updating blog category position:", error);
            throw error;
        }
    };
    return { addBlogCategories, getBlogCategories, updateBlogCategory, deleteBlogCategory, updateCategoryStatus, getBlogByCategoryId, searchBlogCategories, updateBlogCategoryPosition };
}
